import { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import styled, { css } from 'styled-components';

import { getImage } from '@/lib/fetchAPI';

const ScrollPhoneSlides: FC<any> = ({ slides, activeSlide, onHeadingClick }) =>
	slides?.map(
		(
			{
				title,
				description,
				description1,
				description2,
				highlightedText,
				icon,
				iconActive,
				slideId,
			},
			index,
		) => (
			<ScrollSlideOuterWrapper key={`scroll_phone_slide_${index}`}>
				<ScrollSlide
					data-slide
					data-slide-id={slideId}
					$active={slideId === activeSlide}
				>
					<ScrollSlideHeading
						onClick={() => {
							if (!onHeadingClick) return;

							onHeadingClick(slideId === activeSlide ? 0 : slideId);
						}}
					>
						<ScrollSlideIcon>
							<img
								alt={title}
								src={getImage(
									slideId === activeSlide && iconActive?.url
										? iconActive
										: icon,
								)}
							/>
						</ScrollSlideIcon>

						<ScrollSlideTitle>{title}</ScrollSlideTitle>
					</ScrollSlideHeading>

					<ScrollSlideDescription>
						<ReactMarkdown>{description ?? description1}</ReactMarkdown>

						{description2 ? (
							<ChatbotSlideSecondDescription>
								<ReactMarkdown>{description2}</ReactMarkdown>
							</ChatbotSlideSecondDescription>
						) : null}

						{highlightedText ? (
							<ChatbotSlideHighlighted>
								<ReactMarkdown>{highlightedText}</ReactMarkdown>
							</ChatbotSlideHighlighted>
						) : null}
					</ScrollSlideDescription>
				</ScrollSlide>
			</ScrollSlideOuterWrapper>
		),
	);

const ScrollSlideIcon = styled.div`
	width: 80px;
	height: 80px;
	margin-right: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	@media (max-width: 1240px) {
		width: 50px;
		height: 50px;
	}

	@media (max-width: 799px) {
		width: 40px;
		height: 40px;
		margin-right: 5px;
	}
	img {
		@media (max-width: 799px) {
			max-width: 21px;
			max-height: 21px;
		}
	}
`;

const ScrollSlideDescription = styled.div`
	padding-left: 90px;
	font-family: var(--font-sans);
	color: var(--color-black);
	font-size: 18px;
	line-height: 25px;

	> p {
		margin-bottom: 12px;
	}

	@media (max-width: 1240px) {
		font-size: 14px;
		line-height: 18px;
		padding-left: 60px;
	}

	@media (max-width: 799px) {
		padding-left: 45px;
		padding-right: 25px;
		color: var(--color-purple-50);
		max-height: 0;
		opacity: 0;
		transition: all 0.4s ease-in-out;
	}
`;

const ScrollSlideOuterWrapper = styled.div`
	height: 600px;

	@media (max-width: 1240px) {
		width: 100%;
	}

	@media (max-width: 799px) {
		height: auto;
	}
`;

const ScrollSlide = styled.div<{ $active: boolean }>`
	width: 450px;
	padding: 30px 50px 60px 10px;
	border-radius: 70px;
	border: 1px solid rgba(255, 255, 255, 0.4);
	background: rgba(205, 201, 227, 0.1);
	position: relative;
	overflow: hidden;
	top: 0;
	left: 0;
	opacity: 0.4;
	transition: opacity 0.4s ease-in-out;

	@media (max-width: 1240px) {
		width: 285px;
		padding: 30px 20px 50px 15px;
	}

	@media (max-width: 799px) {
		width: 100%;
		border-radius: 0;
		background: transparent;
		padding: 12px 0;
		border: none;
		border-bottom: 1px solid var(--color-white-40);
		margin-bottom: 0;
		opacity: 1;
	}

	${({ $active }) =>
		$active &&
		css`
			opacity: 1;

			${ScrollSlideDescription} {
				@media (max-width: 799px) {
					max-height: 200px;
					opacity: 1;
				}
			}

			&:after {
				@media (max-width: 799px) {
					transform: rotate(180deg);
				}
			}
		`}
	&:after {
		content: '';
		width: 80%;
		height: 100%;
		display: block;
		position: absolute;
		top: 60px;
		left: 0;
		right: 0;
		margin: 0 auto;
		mix-blend-mode: normal;
		background: rgba(205, 201, 227, 0.01);
		opacity: 0.2;
		box-shadow: 0px 0px 80px rgba(205, 201, 227, 0.2);
		border-radius: 70px;

		@media (max-width: 799px) {
			width: 10px;
			height: 10px;
			background: url(/images/chatbots/arrow-down.svg) no-repeat center;
			opacity: 1;
			right: 0;
			left: auto;
			top: 25px;
		}
	}
`;

const ScrollSlideHeading = styled.div`
	display: flex;

	@media (max-width: 799px) {
		align-items: center;
	}
`;

const ScrollSlideTitle = styled.div`
	font-family: var(--font-sans);
	color: var(--color-black);
	font-size: 28px;
	line-height: 35px;
	margin-bottom: 15px;
	max-width: 260px;
	padding-top: 24px;

	@media (max-width: 1240px) {
		padding-top: 10px;
		font-size: 24px;
		line-height: 32px;
		margin-bottom: 10px;
	}

	@media (max-width: 799px) {
		padding: 0;
		margin: 0;
		max-width: 100%;
		font-size: 16px;
		line-height: 25px;
	}
`;

const ChatbotSlideSecondDescription = styled.span`
	display: block;
	margin-top: 12px;
	color: var(--color-purple-50);
`;

const ChatbotSlideHighlighted = styled.span`
	display: block;
	color: var(--color-purple-50);
	margin-top: 12px;
`;

export default ScrollPhoneSlides;
